import type { PairedOrder } from './useSelectedOrders'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { useCloseModal, useConfigureModalController, useModalController } from '@/atoms'
import { useReadOnly } from '@/hooks'

import { useSelectedOrders } from './useSelectedOrders'

export type ModalMode = 'empty' | 'single' | 'bulk'
type ModalState = 'ready' | 'pending' | 'invalidForEmptyOrders'
export type OrdersDistribution = 'non-recurrent' | 'recurrent' | 'mixed'

interface ModalData {
  mode: ModalMode
  ordersDistribution: OrdersDistribution
  deleteAlsoPaired: boolean
  targetRange: uui.domain.ui.forms.TargetRange
  pairedOrders?: PairedOrder[]
  selectedOrders: uui.domain.client.rm.ExtendedOrderStep[]
}

export const modalId = 'deleteOrder'

export const useController = () => useModalController<ModalData, ModalState>(modalId)
export const useConfigureController = (orderStepIds: string[]) => {
  const close = useCloseModal()
  const { readonly } = useReadOnly()
  const { selectedOrders, pairedOrders } = useSelectedOrders(orderStepIds)

  const ordersDistribution = useMemo((): OrdersDistribution => {
    const selectedOrderStepsMap = new Set(selectedOrders.map(order => order.id))

    const isAllRecurrentOrders = selectedOrders.every(order => Boolean(order.order.recurrenceId))
    if (isAllRecurrentOrders) return 'recurrent'

    const isAllNonRecurrentOrders = selectedOrders.every(order => !order.order.recurrenceId)
    if (isAllNonRecurrentOrders) return 'non-recurrent'

    const ordersPD = selectedOrders.filter(order => Boolean(order.order.type === 'pd'))
    if (ordersPD.length > 0) {
      const allPDInsideTheSelection = ordersPD.reduce<boolean>((acc, order) => {
        if (order.order.type !== 'pd') return acc
        if (order.order.pickup && !selectedOrderStepsMap.has(order.order.pickup.id)) return false
        if (order.order.delivery && !selectedOrderStepsMap.has(order.order.delivery.id))
          return false
        return acc
      }, true)

      if (!allPDInsideTheSelection) {
        return 'mixed'
      }
    }

    return 'mixed'
  }, [selectedOrders])

  const getModalMode = useCallback((): ModalMode => {
    if (selectedOrders.length > 1) return 'bulk'
    if (selectedOrders.length > 0) return 'single'
    return 'empty'
  }, [selectedOrders.length])

  // INITIALIZATION

  const [options] = useState(() => ({
    createInitialState: () => ({
      close,
      data: {
        targetRange: 'ORDER' as const,
        pairedOrders,
        selectedOrders,
        ordersDistribution,
        mode: getModalMode(),
        deleteAlsoPaired: true,
      },
    }),
  }))

  const ctrl = useConfigureModalController<ModalData, ModalState>(modalId, options)
  const {
    data: { mode },
    status,
    update,
    updateData,
  } = ctrl

  // EFFECTS

  // External transaction
  useEffect(() => {
    switch (readonly) {
      case true:
        update({ status: 'pending' })
        break

      case false:
        if (status !== 'pending') return

        update({ status: 'ready' })
        break
    }
  }, [readonly, status, update])

  // Invalid state management
  useEffect(() => {
    updateData({ mode: getModalMode() })
  }, [getModalMode, updateData])

  // Invalid state management
  useEffect(() => {
    if (mode === 'empty') {
      update({ invalid: true, status: 'invalidForEmptyOrders' })
      return
    }

    update({ invalid: false, status: 'ready' })
  }, [mode, update])

  return ctrl
}
